import React, { useEffect } from 'react'
import { Navigate, useParams } from 'react-router'
import { CreditApplicationStatus, UnderwritingOffer } from '_gql'

import { FormattedMessage } from '@divvy-web/i18n'
import WistiaPlayer from '@divvy-web/skylab.wistiaplayer'

import { LocalExperimentProvider, useExperiment } from '@sputnik/experiments'
import PollingPageContent from '@sputnik/pages/PostApplicationPages/PollingPageContent'
import { GetDecisionAndStatusForPolling } from '@sputnik/pages/PostApplicationPages/SubmittingApplicationPollingPage/GetDecisionAndStatusForPolling.gql'
import { MAJOR_ACCOUNT_CHANGE, MIGRATION, PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'

export interface ReviewingApplicationPollingPageContentProps {
  stopPolling: () => void
  decisionAndStatusData: GetDecisionAndStatusForPolling
  isReadyToNavigate: boolean
}

export const ReviewingApplicationPollingPageContent = ({
  stopPolling,
  decisionAndStatusData,
  isReadyToNavigate,
}: ReviewingApplicationPollingPageContentProps) => {
  const { creditApplication, underwritingDeclined, frozenAuthorizedSigner, decisions } = decisionAndStatusData ?? {}
  const { companyDivvyUuid, status, recordType } = creditApplication ?? {}

  const { behaviorName, isInExperiment } = useExperiment({
    experiment: 'beep-credit-application-accept-offer-flow-experiment',
    inputId: companyDivvyUuid ?? '',
    bucketingKey: 'companyDivvyUuid',
  })
  const isUWPageRedesign = isInExperiment && behaviorName === 'presentedRedesignedOffersFlow'
  const { appId } = useParams()
  const appPath = `/app/${appId}`

  const isPreSubmitStatus =
    !!status &&
    [
      CreditApplicationStatus.NOT_STARTED,
      CreditApplicationStatus.IN_PROGRESS,
      CreditApplicationStatus.AWAITING_SIGNATURE,
    ].includes(status)
  const isMajorAccountChange = recordType === MAJOR_ACCOUNT_CHANGE
  const isMigration = recordType === MIGRATION
  const hasNoDecisions = !decisions || decisions?.length === 0 || decisions?.length > 3
  const hasSingleDecision = decisions?.length === 1
  const hasMultipleDecisions = decisions && decisions?.length >= 2
  const hasSelectedDecision = !!decisions?.find((decision) => decision?.userSelectedAt)
  const isRejectedStatus = status === CreditApplicationStatus.REJECTED
  const isMoreInfoNeededStatus = status === CreditApplicationStatus.MORE_INFO_NEEDED
  const standardLineOffer = decisions?.find((decision) => decision?.offerType === UnderwritingOffer.FIXED_LINE)
  const variableLineOffer = decisions?.find((decision) => decision?.offerType === UnderwritingOffer.VARIABLE_LINE)
  const securedLineOffer = decisions?.find((decision) => decision?.offerType === UnderwritingOffer.SECURED_LINE)
  const offersPath = `/app/${appId}/${PATHNAME_CONSTS.OFFERS_PATH}`

  /* tracking start */

  const { track } = useWebTracking()

  useEffect(() => {
    track('se_signup__processing_application__presented')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* tracking end */

  if (isReadyToNavigate) {
    window.sessionStorage.removeItem('submissionPendingUrl')
    stopPolling()
    switch (true) {
      case isMajorAccountChange:
        return (
          <Navigate
            replace
            to={PATHNAME_CONSTS.DASHBOARD_PATH}
          />
        )
      case isMigration:
        return (
          <Navigate
            replace
            to={`${appPath}/${PATHNAME_CONSTS.MIGRATION_SUCCESS_PATH}`}
          />
        )
      case frozenAuthorizedSigner:
        return (
          <Navigate
            replace
            to={`${appPath}/${PATHNAME_CONSTS.FROZEN_CREDIT_PATH}`}
          />
        )
      case underwritingDeclined || isRejectedStatus:
        return (
          <Navigate
            replace
            to={`/app/${appId}/${PATHNAME_CONSTS.NO_OFFER_PATH}`}
          />
        )
      case isMoreInfoNeededStatus:
        return (
          <Navigate
            replace
            to={PATHNAME_CONSTS.DASHBOARD_PATH}
          />
        )
      case hasNoDecisions:
        return (
          <Navigate
            replace
            to={`${appPath}/${PATHNAME_CONSTS.WORKING_OUT_DETAILS_PATH}`}
          />
        )
      case hasMultipleDecisions && !hasSelectedDecision:
        return <Navigate to={offersPath} />
      case hasSingleDecision && !hasSelectedDecision:
        if (isUWPageRedesign) {
          return <Navigate to={offersPath} />
        } else if (standardLineOffer) {
          return <Navigate to={`${offersPath}/${PATHNAME_CONSTS.STANDARD_CREDIT_LINE_PATH}`} />
        } else if (variableLineOffer) {
          return <Navigate to={`${offersPath}/${PATHNAME_CONSTS.VARIABLE_CREDIT_LINE_PATH}`} />
        } else if (securedLineOffer) {
          return <Navigate to={`${offersPath}/${PATHNAME_CONSTS.PREPAY_OFFER_PATH}`} />
        }
        break
      case isPreSubmitStatus && !hasSelectedDecision:
        return (
          <Navigate
            replace
            to={`${appPath}/${PATHNAME_CONSTS.SUBMISSION_ERROR_PATH}`}
          />
        )
      default:
        return (
          <Navigate
            replace
            to={PATHNAME_CONSTS.DASHBOARD_PATH}
          />
        )
    }
  }

  return (
    <PollingPageContent
      mainText={
        <FormattedMessage
          defaultMessage={`{isPreSubmitStatus, select, 
            true {We're submitting your application}
            other {We're reviewing your application}
          }`}
          id='sputnik.ReviewingApplicationPollingPageContent__S737h6'
          values={{ isPreSubmitStatus }}
        />
      }
      subText={
        <FormattedMessage
          defaultMessage='We should have a decision for you soon.'
          id='sputnik.ReviewingApplicationPollingPageContent__zrXnI+'
        />
      }
    >
      <WistiaPlayer
        autoplay
        videoId='3371q71man'
      />
    </PollingPageContent>
  )
}

export default (props: ReviewingApplicationPollingPageContentProps) => (
  <LocalExperimentProvider
    experiment='beep-credit-application-accept-offer-flow-experiment'
    inputId={props.decisionAndStatusData.creditApplication?.companyDivvyUuid ?? ''}
  >
    <ReviewingApplicationPollingPageContent {...props} />
  </LocalExperimentProvider>
)
